import {
  type Option,
  type ColumnOrConstantProps,
  type UnaryBoolean,
  ComponentType,
  getUnaryBooleanValue,
} from "@hightouch/formkit";
import {
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import type { ControllerField, ControllerRenderer, SharedProps } from ".";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Combobox,
  GroupedCombobox,
  IconButton,
  MultiSelect,
  Radio,
  RadioGroup,
  RefreshIcon,
  Row,
  Select,
  Switch,
  TextInput,
  Tooltip,
  Text,
} from "@hightouch/htui";
import { isBoolean, isObject } from "lodash";
import { Controller } from "react-hook-form";
import { getExpectedColumnOptions } from "src/events/utils/analyzed-event-schemas";

// Subtypes of the Column or Constant component
enum CocType {
  ColumnModel,
  ColumnCustom,
  CreatableSelect,
  QuerySelect,
  MultiSelect,
  Select,
  RadioGroup,
  TextInput,
}

export const FKColumnOrConstant = ({
  constantComponentType,
  creatable,
  createLabelPrefix = "object",
  error,
  name,
  options,
  type,
  multi,
  searchable,
  isDisabled,
  query,
  placeholder,
}: ColumnOrConstantProps & {
  creatable?: UnaryBoolean;
  createLabelPrefix?: string;
  searchable?: boolean;
  placeholder?: string;
} & SharedProps) => {
  const { model, columns, isEventForwardingForm } = useFormkitContext();
  const { refreshEventSchemas, loadingModel } = useFormkitDataContext();
  const isEventModel = Boolean(model?.isEventModel);

  // User-input options
  const [customOptions, setCustomOptions] = useState<Option[]>([]);

  // Create a new custom option
  const createOption = (value: string) => {
    // Do nothing if it is already a custom value or already a preset value
    if (!combinedOptions.map((option) => option.value).includes(value)) {
      setCustomOptions([...customOptions, { label: value, value: value }]);
    }
  };

  // Options predefined by the form
  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  const existingOptions: Option[] = useMemo(
    () => (query.enabled ? query.data : options) ?? [],
    [query.data, options],
  );

  // Values that will be ignored if the user manually enters them
  const existingValues = useMemo(
    () => existingOptions.map((option) => option.value),
    [existingOptions],
  );

  // Combined Option list should include all options, both existing and custom
  const combinedOptions: Option[] = useMemo(
    () => [
      ...existingOptions,
      ...customOptions.filter(
        (option) => !existingValues.includes(option.value),
      ),
    ],
    [existingOptions, existingValues, customOptions],
  );

  // Items in `columns` have `options` field as optional,
  // but `GroupedCombobox` expects it to be required
  const optionGroups = useMemo(() => {
    return (columns ?? []).map((group) => ({
      ...group,
      options: group.options ?? [],
    }));
  }, [columns]);

  const flatColumnOptions: Option[] = useMemo(() => {
    return optionGroups
      .flatMap((group) => group.options ?? [])
      .map((o) => ({
        ...o,
        description: o.description ?? undefined,
        type: o.type ?? undefined,
      }));
  }, [optionGroups]);

  const isSelectOrNothing = [ComponentType.Select, undefined].includes(
    constantComponentType,
  );
  const isMulti = multi && isSelectOrNothing;
  const isSelect = options && isSelectOrNothing;
  const isRadioGroup =
    options && ComponentType.RadioGroup === constantComponentType;
  const isSearchable = searchable && isSelectOrNothing;

  // Render a grouped combobox
  const groupedCombobox = (field: ControllerField) =>
    isEventModel ? (
      <Combobox
        emptyOptionsMessage="Type in a field name and Hightouch will use it as an expected column"
        isClearable={true}
        isDisabled={isDisabled}
        isInvalid={Boolean(error)}
        options={getExpectedColumnOptions(
          flatColumnOptions ?? [],
          field.value?.from,
        )}
        placeholder={
          (flatColumnOptions ?? []).length
            ? "Select or enter a column…"
            : "Enter an expected column…"
        }
        supportsCreatableOptions={true}
        value={field.value?.from}
        onChange={(value) => {
          field.onChange({
            from: value || undefined,
          });
        }}
        onCreateOption={async (value) => {
          field.onChange({
            from: value || undefined,
          });
        }}
        createOptionMessage={(value) =>
          `Select "${value}" as an expected column value…`
        }
      />
    ) : (
      <GroupedCombobox
        isClearable={true}
        isDisabled={isDisabled}
        isInvalid={Boolean(error)}
        isLoading={loadingModel}
        optionGroups={optionGroups}
        placeholder="Select a column…"
        value={field.value?.from}
        onChange={(value) => {
          field.onChange({
            from: value || undefined,
          });
        }}
      />
    );
  const comboBox = (field: ControllerField) => (
    <Combobox
      isClearable={true}
      isDisabled={isDisabled}
      isInvalid={Boolean(error)}
      isLoading={query.isFetching}
      options={combinedOptions}
      placeholder="Select an option…"
      value={field.value}
      onChange={(value) => {
        field.onChange(value ?? null);
      }}
      supportsCreatableOptions={
        getUnaryBooleanValue(creatable ?? false) || isEventModel
      }
      onCreateOption={async (value) => {
        field.onChange(value);
        createOption(value);
      }}
      createOptionMessage={(value) => `Create ${createLabelPrefix} "${value}"…`}
    />
  );
  const multiSelect = (field: ControllerField) => (
    <MultiSelect<Option, string>
      isClearable={!query.enabled}
      isDisabled={isDisabled}
      isInvalid={Boolean(error)}
      options={combinedOptions}
      isLoading={query.enabled ? query.isFetching : false}
      value={Array.isArray(field.value) ? field.value : []}
      placeholder="Select options…"
      onChange={(newValue) => {
        field.onChange(newValue ?? []);
      }}
    />
  );
  const isColumnn = (field: ControllerField) =>
    // Multi select is only avaliable on Select component.
    !(isMulti && Array.isArray(field.value)) &&
    typeof field.value === "object" &&
    ![null, undefined].includes(field.value);
  // Complex ternary logic for determining the subtype of this component
  const componentType = (field: ControllerField) =>
    isColumnn(field)
      ? model
        ? CocType.ColumnModel
        : CocType.ColumnCustom
      : isSelect
        ? creatable
          ? CocType.CreatableSelect
          : isMulti
            ? CocType.MultiSelect
            : isSearchable
              ? CocType.QuerySelect
              : CocType.Select
        : isRadioGroup
          ? CocType.RadioGroup
          : CocType.TextInput;

  const onSwitchChange = (field: ControllerField) => (value: boolean) => {
    const newValue = value
      ? { from: undefined }
      : constantComponentType === ComponentType.RadioGroup
        ? options[0].value
        : options
          ? // Dropdown
            isMulti
            ? []
            : null
          : // Input field
            "";
    field.onChange(newValue);
  };

  const renderer: Record<CocType, ControllerRenderer> = {
    [CocType.ColumnModel]: ({ field }) => (
      <Box alignItems="center" display="flex" gap={2}>
        {groupedCombobox(field)}

        {refreshEventSchemas && (
          <Button
            aria-label="Refresh fields seen in recent events"
            icon={RefreshIcon}
            onClick={refreshEventSchemas}
            isDisabled={isDisabled}
          >
            Refresh
          </Button>
        )}
      </Box>
    ),
    [CocType.ColumnCustom]: ({ field }) => (
      <TextInput
        isDisabled={isDisabled}
        isInvalid={Boolean(error)}
        placeholder="Enter a column…"
        value={field.value?.from ?? ""}
        onChange={({ target: { value } }) => {
          field.onChange({
            from: value || undefined,
          });
        }}
        type={type}
      />
    ),
    [CocType.CreatableSelect]: ({ field }) => (
      <Row>
        {comboBox(field)}
        {query.enabled ? (
          <Tooltip message="Reload options">
            <IconButton
              aria-label="Reload options"
              isLoading={query.isFetching}
              isDisabled={isDisabled}
              icon={RefreshIcon}
              onClick={() => {
                if (query.refetch) {
                  query.refetch();
                }
              }}
            />
          </Tooltip>
        ) : (
          <></>
        )}
      </Row>
    ),
    [CocType.QuerySelect]: ({ field }) => (
      <Box alignItems="center" display="flex" gap={2}>
        {(isMulti ? multiSelect : comboBox)(field)}
        <Button
          icon={RefreshIcon}
          variant="secondary"
          isDisabled={isDisabled}
          onClick={query.refetch}
        >
          Refresh
        </Button>
      </Box>
    ),
    [CocType.MultiSelect]: ({ field }) => multiSelect(field),
    [CocType.Select]: ({ field }) => (
      <Select
        isClearable={true}
        isDisabled={isDisabled}
        isInvalid={Boolean(error)}
        options={combinedOptions}
        placeholder="Select an option…"
        value={field.value}
        onChange={(value) => {
          field.onChange(value ?? undefined);
        }}
      />
    ),
    [CocType.RadioGroup]: ({ field }) => {
      const selectedRadioGroupIndex =
        Array.isArray(options) &&
        constantComponentType === ComponentType.RadioGroup
          ? options.findIndex(
              (option) => option.value === (field.value ?? undefined),
            )
          : -1;
      return (
        <RadioGroup
          isDisabled={isDisabled}
          value={String(selectedRadioGroupIndex)}
          onChange={(indexString) => {
            const option = combinedOptions.find(
              (_, index) => String(index) === indexString,
            );
            field.onChange(option?.value ?? null);
          }}
        >
          {combinedOptions.map((option, index) => (
            <Radio
              key={
                isBoolean(option.value) || isObject(option.value)
                  ? index
                  : (option.value ?? index)
              }
              description={option.description}
              label={option.label}
              value={String(index)}
            />
          ))}
        </RadioGroup>
      );
    },
    [CocType.TextInput]: ({ field }) => (
      <TextInput
        isDisabled={isDisabled}
        isInvalid={Boolean(error)}
        placeholder={placeholder ?? "Enter a value…"}
        value={field.value}
        onChange={field.onChange}
        type={type}
      />
    ),
  };

  const skeleton = ({ field }: { field: ControllerField }) => (
    <Row display="flex" justifyContent="space-between">
      {renderer[componentType(field)]({ field })}

      <Box>
        <Text
          textTransform="uppercase"
          size="sm"
          color="text.secondary"
          fontWeight="semibold"
        >
          {isEventForwardingForm ? "Use property" : "Use column"}
        </Text>

        <Switch
          aria-label={`Use ${isEventForwardingForm ? "property" : "column"}`}
          isChecked={isColumnn(field)}
          isDisabled={isDisabled}
          onChange={onSwitchChange(field)}
        />
      </Box>
    </Row>
  );

  return <Controller name={name} render={skeleton} />;
};
